<template>
  <div
    class="conversation-input pl-5 pt-3 pb-3 pr-5"
    :class="{ 'emoji-open': showEmojiPicker, 'keyboard-open': isKeyboardOpen, 'switching': switchingMode !== 'none' || holdEmojiUntilKeyboard }"
    :style="{
      '--composer-inset': composerInsetCss,
      '--keyboard-inset': keyboardInsetCss,
      '--bottom-inset': bottomInsetCss,
      '--emoji-panel-max-height': emojiPanelMaxHeightCss,
      '--emoji-picker-height': emojiPickerHeightCss,
    }"
    v-if="currentContactId && !isUserBlocked"
  >
        <div class="composer-main pb-3">
      <div class="input-group">
        <textarea
          ref="textareaRef"
          :value="message"
          @input="handleInput"
          class="form-control message-input"
          @keydown="handleKeyDown"
          @focus="handleTextareaFocus"
          @blur="handleTextareaBlur"
          maxlength="1000"
          rows="1"
          :placeholder="t('messages.enter_message')"
        ></textarea>
        <div class="input-group-append" v-show="shouldShowUploadButton">
          <button 
            type="button" 
            class="btn btn-secondary"
            @click="triggerFileUpload"
            :disabled="uploading"
            :title="t('messages.upload_photo')"
          >
            <i class="fa fa-picture-o" v-if="!uploading"></i>
            <i class="fa fa-spinner fa-spin" v-else></i>
          </button>
        </div>
        <input 
          ref="fileInput"
          type="file" 
          multiple 
          accept="image/*"
          style="display: none"
          @change="handleFileUpload"
          :disabled="uploading"
        >
        <div class="input-group-append position-relative" @click.stop="toggleEmojiPicker">
          <button 
            type="button" 
            class="btn btn-secondary btn-emoji-picker"
            :title="isMobile
              ? (showEmojiPicker ? t('messages.show_keyboard') : t('messages.add_emoji'))
              : t('messages.add_emoji')"
          >
            <i v-if="isMobile && showEmojiPicker" class="fa fa-keyboard-o"></i>
            <i v-else class="fa fa-smile-o"></i>
          </button>
        </div>
        <div class="input-group-append">
          <button type="button" class="btn btn-secondary" @click="onSend" :disabled="sending || (!message.trim() && (!uploadedFiles.length || textRequired))" :title="textRequired && !message.trim() ? t('messages.reply_text_required') : t('messages.send')">
            <i v-if="sending" class="fa fa-spinner fa-spin"></i>
            <i v-else class="fe fe-send"></i>
          </button>
        </div>
      </div>
      
      <div class="text-right mt-1" v-if="message.length > 800">
        <small class="text-muted" :class="{ 'text-danger': message.length > 950 }">
          {{ message.length }}/1000
        </small>
      </div>
    </div>

    <div ref="emojiPanelRef" class="emoji-panel" :class="{ 'is-open': showEmojiPicker || uploadedFiles.length > 0 || holdEmojiUntilKeyboard || isKeyboardOpen, 'keyboard-open': isKeyboardOpen, 'switching': switchingMode !== 'none' || holdEmojiUntilKeyboard }">
      <KeepAlive>
        <EmojiPicker
          v-if="showEmojiPicker"
          :show-picker="showEmojiPicker"
          :inline="true"
          @select="addEmoji"
          @close="closeEmojiPicker"
        />
      </KeepAlive>

      <div v-if="!showEmojiPicker && uploadedFiles.length" class="uploaded-files-preview reply-quote-bar px-3 py-2 mb-1">
        <small class="text-muted uploaded-files-count d-block mb-1">
          {{ t('messages.files_selected', { count: uploadedFiles.length }) }}
        </small>
        <div class="d-flex flex-row flex-nowrap flex-grow-1 file-preview-row">
          <button
            type="button"
            class="file-preview-add"
            :title="t('messages.upload_photo')"
            :aria-label="t('messages.upload_photo')"
            @click="triggerFileUpload"
          >
            <i class="fe fe-plus"></i>
          </button>
          <div 
            v-for="(file, index) in uploadedFiles" 
            :key="index"
            class="file-preview-item position-relative"
          >
            <img 
              :src="file.preview" 
              :alt="file.name"
              class="img-thumbnail file-preview-thumb"
            >
            <button 
              type="button" 
              class="file-preview-remove position-absolute"
              @click="removeFile(index)"
            >
              <i class="fe fe-x"></i>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Сообщение о блокировке -->
  <div 
    v-if="currentContactId && isUserBlocked"
    class="conversation-input pl-5 pt-3 pb-3 pr-5 text-center text-muted"
  >
    <div class="text-muted pb-3">
      <i class="fa fa-exclamation-triangle text-danger mr-2"></i>
      {{ blockMessage }}
      
      <!-- Кнопка Unblock для пользователя, который заблокировал -->
      <div v-if="showUnblockButton" class="mt-2">
        <button 
          type="button" 
          class="btn btn-secondary"
          @click="handleUnblock"
        >
          {{ t('messages.unblock_user') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, onMounted, onUnmounted, nextTick, computed } from 'vue';
import { useTypingIndicator } from '../composables/useTypingIndicator';
import { useI18n } from '../composables/useI18n';
import { logger } from '../utils/logger';
import { haptic } from '../utils/haptics';
import { processReplyPreviewText } from '../utils/textHelpers';
import { useMessageDrafts } from '../composables/useMessageDrafts';
import EmojiPicker from './EmojiPicker.vue';

const { t } = useI18n();

const props = defineProps<{
  currentContactId: string | number | null;
  replyTo?: any;
  editText?: string;
  isUserBlocked?: boolean;
  blockType?: 'blocked_by_user' | 'user_blocked' | null;
  textRequired?: boolean;
}>();

const emit = defineEmits<{ 
  (e: 'send', payload: { text: string; files?: File[]; replyTo?: any }): void; 
  (e: 'input', isClearingAfterSend: boolean): void;
  (e: 'cancelReply'): void;
  (e: 'unblock'): void;
}>();

const getReplyPreview = (item: any): string => {
  if (!item) return '';
  if (item.text?.startsWith('[gift]')) return '🎁 Gift';
  if (!item.text && item.attachments?.length) return '<i class="fe fe-image mr-1"></i>' + t('messages.photo');
  return processReplyPreviewText(item.text);
};

const message = ref('');
const textareaRef = ref<HTMLTextAreaElement | null>(null);
const emojiPanelRef = ref<HTMLDivElement | null>(null);
const showEmojiPicker = ref(false);
const uploadedFiles = ref<Array<{file: File; preview: string; name: string; objectUrl?: string}>>([]);
const uploading = ref(false);
const sending = ref(false);
const fileInput = ref<HTMLInputElement | null>(null);
const isClearingAfterSend = ref(false); // Флаг для предотвращения ложного typing при очистке после отправки
defineExpose({
  restoreText: (text: string, files: File[] = []) => {
    message.value = message.value.trim() ? `${text}\n${message.value}` : text;
    const restored = files.map(file => {
      const objectUrl = URL.createObjectURL(file);
      return { file, preview: objectUrl, name: file.name, objectUrl };
    });
    uploadedFiles.value = [...restored, ...uploadedFiles.value];
  }
});

// ===== Draft persistence =====
const { loadDraft, saveDraft: saveDraftNow } = useMessageDrafts();
let draftSaveTimeout: ReturnType<typeof setTimeout> | null = null;

const isEditing = computed(() => props.editText !== undefined && props.editText !== null && props.editText !== '');

const debouncedSaveDraft = (contactId: string | number | null, text: string) => {
  if (draftSaveTimeout) {
    clearTimeout(draftSaveTimeout);
    draftSaveTimeout = null;
  }
  draftSaveTimeout = setTimeout(() => {
    saveDraftNow(contactId, text);
  }, 300);
};

const keyboardInsetPx = ref(0);
const holdEmojiUntilKeyboard = ref(false);
const switchingMode = ref<'none' | 'toEmoji' | 'toKeyboard'>('none');
const frozenInsetPx = ref(0);
// Default keyboard height fallback (typical mobile keyboard is ~280px).
// This prevents jumps when emoji is opened before the keyboard was ever shown.
const DEFAULT_KEYBOARD_HEIGHT_PX = 280;
// Anything smaller is not a keyboard: gesture-nav bar, address-bar show,
// rounding. Treating it as keyboard produced a false `isKeyboardOpen` and
// collapsed the emoji panel to that tiny inset height.
const KEYBOARD_MIN_INSET_PX = 120;
const lastKeyboardInsetPx = ref(0);
// Базовая линия visualViewport без клавиатуры. Клавиатура на ВСЕХ семействах
// устройств (resizes-visual, resizes-content, iOS) проявляется как УСАДКА
// vv.height от этой линии. Прежняя формула innerHeight - vv.height - offsetTop
// слепа там, где layout viewport сжимается вместе с клавиатурой
// (resizes-content): она давала ~0 при открытой клавиатуре, из-за чего
// удержание emoji не снималось и пикер оставался висеть над клавиатурой
// (стабильный оверлей emoji+keyboard).
const vvBaselineHeightPx = ref(typeof window !== 'undefined' ? Math.round(window.visualViewport?.height ?? window.innerHeight) : 0);
const vvBaselineWidthPx = ref(typeof window !== 'undefined' ? Math.round(window.visualViewport?.width ?? window.innerWidth) : 0);
// Сколько пространства клавиатуры layout viewport уже исключил сам
// (resizes-content): ICB (documentElement.clientHeight) сжимается синхронно
// с клавиатурой. На resizes-visual/iOS ICB не меняется → 0. Видимая высота
// панели-спейсера = K − layoutExcludedPx, иначе в кадр синхронного сжатия ICB
// композер прыгнул бы вверх на высоту клавиатуры.
const icbBaselineHeightPx = ref(typeof window !== 'undefined' ? document.documentElement.clientHeight : 0);
const layoutExcludedPx = ref(0);
// Debounce-таймер «осевшей» клавиатуры: lastKeyboardInsetPx запоминает только
// финальную высоту ПОЛНОСТЬЮ открытой клавиатуры. Иначе при закрытии сюда
// попадает промежуточное значение анимации (~120–200px) и emoji-панель /
// файловое превью наследуют заниженную высоту.
let keyboardSettleTimerId: ReturnType<typeof setTimeout> | null = null;
const viewportHeightPx = ref<number>(typeof window !== 'undefined' ? window.innerHeight : 0);
const emojiPanelMeasuredHeightPx = ref(0);

const isKeyboardOpen = computed(() => keyboardInsetPx.value > 0);

// isMobile должен быть реактивен к ресайзам и поворотам экрана.
// Раньше был computed без реактивных зависимостей — значение фиксировалось на старте
// и не обновлялось при повороте устройства (landscape ↔ portrait мог пересекать 768px).
const viewportWidthPx = ref<number>(typeof window !== 'undefined' ? window.innerWidth : 0);
const isMobile = computed(() => viewportWidthPx.value < 768);

// Сообщение о блокировке в зависимости от типа
const blockMessage = computed(() => {
  if (!props.isUserBlocked) return '';

  switch (props.blockType) {
    case 'user_blocked':
      return t('messages.you_blocked_this_user');
    case 'blocked_by_user':
      return t('messages.you_have_been_blocked');
    default:
      return t('messages.messaging_not_available');
  }
});

// Показывать кнопку Unblock только если текущий пользователь заблокировал другого
const showUnblockButton = computed(() => {
  return props.isUserBlocked && props.blockType === 'user_blocked';
});

// Функция для разблокировки
const handleUnblock = () => {
  emit('unblock');
};

const emojiPanelHeightPx = computed(() => {
  const base = viewportHeightPx.value || window.innerHeight;
  if (!isMobile.value) {
    return 176;
  }

  const desired = Math.round(base * 0.42);
  return Math.max(220, Math.min(360, desired));
});

const emojiPanelOpenHeightPx = computed(() => {
  if (!isMobile.value) return emojiPanelHeightPx.value;

  // Viber стиль: панель ВСЕГДА равна высоте клавиатуры (измеренной или дефолтной).
  // Тогда композер стоит на одной и той же Y-позиции при показе и клавиатуры,
  // и emoji-панели, и переключение между ними не двигает строку ввода.
  //
  // Когда панель УЖЕ открыта, высоту берём из «осевшей» клавиатуры
  // (lastKeyboardInsetPx), а не из живого инсета: при закрытии клавиатуры
  // keyboardInsetPx плавно убывает до 0 и панель следовала бы за ним —
  // сжималась, а затем прыгала к запомненной высоте (видимый скачок при
  // переключении keyboard → emoji).
  // holdEmojiUntilKeyboard тоже считаем «открытой панелью»: пока клавиатура
  // поднимается, спейсер должен использовать осевшую высоту K, а не живой
  // растущий inset — иначе K - inset схлопнулся бы до 0 и композер прыгнул
  // на верхний край клавиатуры вместо того, чтобы стоять на месте.
  if (showEmojiPicker.value || uploadedFiles.value.length > 0 || holdEmojiUntilKeyboard.value) {
    return lastKeyboardInsetPx.value || keyboardInsetPx.value || DEFAULT_KEYBOARD_HEIGHT_PX;
  }
  return keyboardInsetPx.value || lastKeyboardInsetPx.value || DEFAULT_KEYBOARD_HEIGHT_PX;
});

const emojiPickerHeightPx = computed(() => {
  const inner = emojiPanelOpenHeightPx.value - 16;
  return isMobile.value ? Math.max(160, inner) : Math.max(120, inner);
});

// Viber-модель: композер стоит на высоте (inset + panelHeight). Чтобы позиция
// не менялась ни в одном состоянии и ни на одном кадре перехода, видимая высота
// панели всегда «добирает» до полной высоты клавиатуры K:
//   клавиатура открыта (inset=K)  -> P=0    (панель схлопнута)
//   клавиатура закрыта (inset=0)  -> P=K    (панель полная)
//   анимация клавиатуры           -> P=K-inset (панель дышит встречно)
const emojiPanelEffectiveHeightPx = computed(() => {
  if (!isMobile.value) return emojiPanelOpenHeightPx.value;
  // Спейсер компенсирует только ту часть клавиатуры, которую layout viewport
  // НЕ исключил сам: resizes-visual/iOS → вся высота K; resizes-content → 0
  // (ICB сжался синхронно с клавиатурой, и панель обязана схлопнуться в тот
  // же кадр, иначе композер прыгнет вверх на высоту клавиатуры).
  return Math.max(0, emojiPanelOpenHeightPx.value - layoutExcludedPx.value);
});

const getMeasuredEmojiPanelHeight = () => {
  const el = emojiPanelRef.value;
  if (!el) return 0;
  const rect = el.getBoundingClientRect();
  return Math.max(0, Math.round(rect.height));
};

const updateMeasuredEmojiPanelHeight = () => {
  emojiPanelMeasuredHeightPx.value = getMeasuredEmojiPanelHeight();
  
  // На мобильных высота панели всегда равна высоте клавиатуры — и когда она
  // открыта, и когда закрыта (через lastKeyboardInsetPx / дефолт), чтобы
  // позиция композера не менялась при переключении keyboard <-> emoji.
  // Во время hold панель тоже держит инлайн-высоту: она находится В ПОТОКЕ
  // (position: static/relative), и именно её flow-высота поднимает композер —
  // sticky bottom на .composer-main не может удержать позицию, когда панель
  // схлопнута (sticky не выходит за пределы containing block). Пустая панель
  // без содержимого (пикер размонтирован) = невидимый спейсер.
  if (isMobile.value && emojiPanelRef.value) {
    // Пустой панели max-height высоту НЕ задаёт (пустой div не растягивается),
    // поэтому спейсер-состояния (включая открытую клавиатуру без пикера)
    // требуют явного инлайн-height. Когда панель закрыта, обязательно чистим
    // инлайн-высоту: она побеждает не-important scoped max-height:0 и оставляет
    // панель приоткрытой на последнем промежуточном значении анимации клавиатуры.
    if (showEmojiPicker.value || uploadedFiles.value.length > 0 || holdEmojiUntilKeyboard.value || isKeyboardOpen.value) {
      emojiPanelRef.value.style.height = `${emojiPanelEffectiveHeightPx.value}px`;
    } else {
      emojiPanelRef.value.style.height = '';
    }
  }
};

const keyboardLiftPx = computed(() => {
  // During mode switches (keyboard <-> emoji), keep the last inset to avoid visual jumps.
  const raw = switchingMode.value !== 'none' ? frozenInsetPx.value : keyboardInsetPx.value;
  if (raw <= 0) return 0;
  return Math.max(0, raw);
});

const composerInsetPx = computed(() => {
  if (!holdEmojiUntilKeyboard.value) return 0;
  return emojiPanelMeasuredHeightPx.value || emojiPanelHeightPx.value;
});

const composerInsetCss = computed(() => `${composerInsetPx.value}px`);
const keyboardInsetCss = computed(() => `${keyboardLiftPx.value}px`);
// max-height ОТКРЫТОЙ панели = полная высота K (не effective): cap не должен
// анимироваться вслед за инлайн-высотой, иначе в кадр восстановления ICB
// (keyboard → emoji на resizes-content) рост панели длился бы 0.3s и композер
// отпрыгивал вниз-вверх. Фактическую высоту задаёт инлайн height = effective.
const emojiPanelMaxHeightCss = computed(() => `${emojiPanelOpenHeightPx.value}px`);
const emojiPickerHeightCss = computed(() => `${emojiPickerHeightPx.value}px`);

const bottomInsetPx = computed(() => {
  if (!isMobile.value) return 0;

  // Открыта панель (emoji/файлы) ИЛИ идёт переход emoji→keyboard.
  // Страховочный механизм: в flow-раскладке позицию композера держит высота
  // самой панели, а sticky-bottom здесь — no-op (равенство при открытой
  // панели, clamp при закрытой). Оставлен как есть, чтобы не менять
  // поведение на устройствах с другим viewport-режимом.
  if (showEmojiPicker.value || uploadedFiles.value.length > 0 || holdEmojiUntilKeyboard.value) {
    return emojiPanelEffectiveHeightPx.value;
  }
  return 0;
});
const bottomInsetCss = computed(() => `${bottomInsetPx.value}px`);

const updateKeyboardInset = () => {
  const vv = window.visualViewport;
  if (!vv) {
    logger.warn('MessageInput: visualViewport not supported, using fallback');
    keyboardInsetPx.value = 0;
    return;
  }

  viewportHeightPx.value = Math.round(vv.height);

  if (!isMobile.value) {
    keyboardInsetPx.value = 0;
    return;
  }

  const vvH = Math.round(vv.height);
  const vvW = Math.round(vv.width);
  const icbH = document.documentElement.clientHeight;

  // Поворот устройства: ширина изменилась — клавиатуры нет, базовые линии
  // пересчитываем заново (иначе усадка высоты при повороте сойдёт за клавиатуру).
  if (vvBaselineWidthPx.value && Math.abs(vvW - vvBaselineWidthPx.value) > 2) {
    vvBaselineWidthPx.value = vvW;
    vvBaselineHeightPx.value = vvH;
    icbBaselineHeightPx.value = icbH;
    layoutExcludedPx.value = 0;
    keyboardInsetPx.value = 0;
    holdEmojiUntilKeyboard.value = false;
    switchingMode.value = 'none';
    frozenInsetPx.value = 0;
    return;
  }
  vvBaselineWidthPx.value = vvW;

  // Клавиатура = усадка visualViewport от базовой линии. Работает на всех
  // семействах устройств. Панорамирование (vv.offsetTop) и скролл страницы
  // высоту vv.height не меняют → ложных срабатываний нет.
  const delta = vvBaselineHeightPx.value - vvH;
  if (delta < 0) {
    // vv вырос выше базы (клавиатура закрылась / адресная строка скрылась) — новая база.
    vvBaselineHeightPx.value = vvH;
    keyboardInsetPx.value = 0;
  } else if (delta >= KEYBOARD_MIN_INSET_PX) {
    keyboardInsetPx.value = delta;
  } else {
    // Малая усадка (стриминговые события подъёма клавиатуры с шагом < 120px):
    // базу НЕ сбрасываем — усадка копится от исходной базы, иначе на последнем
    // кадре детект обнулялся и панель-спейсер оставалась висеть между
    // клавиатурой и композером (пустой зазор со скриншота).
    keyboardInsetPx.value = 0;
  }

  // Сколько пространства layout viewport уже исключил сам (resizes-content):
  // ICB сжимается/растёт синхронно с клавиатурой и адресной строкой. Трекаем
  // НЕЗАВИСИМО от детекта клавиатуры: на закрытии клавиатуры vv-событие может
  // прийти раньше, чем ICB вырастет обратно, — если обнулять excluded по факту
  // «клавиатура закрылась», панель вырастет до K, пока низ модалки ещё прижат
  // к клавиатуре, и композер мигнёт вверх (вспышка зазора при смене
  // keyboard → emoji). Панель = K − усадка ICB привязывает композер к
  // «экранному низу − K» при ЛЮБОМ состоянии ICB. База растягивается только
  // на рост ICB (клавиатура закрылась / адресная строка скрылась).
  if (icbH > icbBaselineHeightPx.value) {
    icbBaselineHeightPx.value = icbH;
  }
  layoutExcludedPx.value = Math.max(0, icbBaselineHeightPx.value - icbH);

  const inset = keyboardInsetPx.value;

  // Высоту запоминаем только когда инсет перестал меняться и клавиатура
  // реально открыта — промежуточные кадры анимации закрытия не записываются.
  if (keyboardSettleTimerId) clearTimeout(keyboardSettleTimerId);
  if (inset > 0) {
    keyboardSettleTimerId = setTimeout(() => {
      keyboardSettleTimerId = null;
      if (keyboardInsetPx.value >= KEYBOARD_MIN_INSET_PX) {
        lastKeyboardInsetPx.value = keyboardInsetPx.value;
        // Клавиатура «осела»: K теперь реальная высота. Удержание снимаем —
        // спейсер дальше держится на isKeyboardOpen (до закрытия клавиатуры).
        if (holdEmojiUntilKeyboard.value) {
          holdEmojiUntilKeyboard.value = false;
          switchingMode.value = 'none';
        }
      }
    }, 300);
  }

  if (switchingMode.value === 'toEmoji' && inset === 0) {
    switchingMode.value = 'none';
    frozenInsetPx.value = 0;
  }
  if (switchingMode.value === 'toKeyboard' && inset > 0) {
    switchingMode.value = 'none';
  }

  // Раннее снятие удержания: клавиатура достигла запомненной высоты.
  if (holdEmojiUntilKeyboard.value && inset >= frozenInsetPx.value && frozenInsetPx.value > 0) {
    holdEmojiUntilKeyboard.value = false;
    switchingMode.value = 'none';
  }

  // Прямая переустановка высоты спейсера: не полагаемся на watch — усадка ICB
  // на части устройств применяется позже vv-события, и без этого спейсер
  // зависал между клавиатурой и композером (пустой зазор).
  if (isMobile.value) {
    nextTick(() => updateMeasuredEmojiPanelHeight());
  }
};

const typing = useTypingIndicator();

const MAX_TEXTAREA_HEIGHT_PX = 140;

const adjustTextareaHeight = () => {
  const el = textareaRef.value;
  if (!el) return;

  el.style.minHeight = '38px';

  // Only auto-adjust height on mobile
  if (!isMobile.value) {
    el.style.height = 'auto';
    el.style.overflowY = 'hidden';
    return;
  }

  el.style.height = 'auto';
  const newHeight = Math.min(el.scrollHeight, MAX_TEXTAREA_HEIGHT_PX);
  el.style.height = `${Math.max(newHeight, 38)}px`;
  el.style.overflowY = el.scrollHeight > MAX_TEXTAREA_HEIGHT_PX ? 'auto' : 'hidden';
};

const revokeObjectUrls = () => {
  const filesToRevoke = [...uploadedFiles.value];
  
  filesToRevoke.forEach(item => {
    try {
      if (item?.objectUrl) {
        URL.revokeObjectURL(item.objectUrl);
        logger.log('Revoked object URL for:', item.name);
      }
    } catch (error) {
      logger.warn('Failed to revoke object URL for:', item.name, error);
    }
  });
  
  // Don't clear array here - let calling function handle it
};

const onSend = async () => {
  if (sending.value) return;
  if (props.isUserBlocked) {
    console.warn('MessageInput: User is blocked, preventing message send');
    return;
  }
  
  const text = message.value.trim();
  const files = uploadedFiles.value.map(item => item.file);
  
  if (!text && (!files.length || props.textRequired)) return;
  
  haptic(10);
  sending.value = true;
  
  try {
    emit('send', { text, files: files.length > 0 ? files : undefined, replyTo: props.replyTo || undefined });
    typing.stopTyping(props.currentContactId as any);
    
    isClearingAfterSend.value = true;
    message.value = '';
    // Очищаем черновик сразу после отправки
    saveDraftNow(props.currentContactId, '');
    setTimeout(() => {
      isClearingAfterSend.value = false;
    }, 600);
    
    const el = textareaRef.value;
    if (el) {
      el.style.height = '38px';
      el.style.overflowY = 'hidden';
    }
    revokeObjectUrls();
    uploadedFiles.value = [];
  } catch (error) {
    console.error('Failed to send message:', error);
  } finally {
    setTimeout(() => {
      sending.value = false;
    }, 100);
  }
};

const handleKeyDown = (event: KeyboardEvent) => {
  if (event.key === 'Enter') {
    const isMobile = window.innerWidth < 768;
    const isShiftPressed = event.shiftKey;

    if (isMobile) {
      nextTick(() => adjustTextareaHeight());
      return;
    } else {
      if (isShiftPressed) {
        nextTick(() => adjustTextareaHeight());
        return;
      } else {
        event.preventDefault();
        onSend();
      }
    }
  }
};

const handleInput = (event: Event) => {
  if (!props.currentContactId) return;
  
  // На мобильных устройствах используем прямой DOM доступ
  // (так же как работает AngularJS в message-sender.js)
  if (isMobile.value && textareaRef.value) {
    message.value = textareaRef.value.value;
  } else {
    // На десктопе используем event.target.value
    const target = event.target as HTMLTextAreaElement;
    message.value = target.value;
  }
  
  adjustTextareaHeight();
  // Передаем флаг очистки после отправки через emit
  emit('input', isClearingAfterSend.value);
};

// Computed property для определения видимости кнопки загрузки фото
const shouldShowUploadButton = computed(() => {
  return message.value.trim().length === 0 && uploadedFiles.value.length === 0;
});

const handleTextareaFocus = () => {
  // На мобильных: если emoji панель открыта, замораживаем на высоту emoji и показываем клавиатуру.
  // ВАЖНО: порядок операций критичен. Раньше здесь вызывался `closeEmojiPicker()`,
  // который обнулял `frozenInsetPx` и `switchingMode`, только что установленные выше.
  // Из-за этого заморозка не работала: `bottomInsetPx` мгновенно падал до 0
  // (emoji уже закрыт, клавиатура ещё не открылась), композер прыгал вниз,
  // затем при открытии клавиатуры — снова вверх. Теперь закрываем emoji-picker
  // ВРУЧНУЮ (через `showEmojiPicker.value = false`), сохраняя заморозку, и запускаем
  // fallback-таймаут на случай, если visualViewport не сообщит об открытии клавиатуры.
  if (isMobile.value && showEmojiPicker.value) {
    frozenInsetPx.value = emojiPanelOpenHeightPx.value;
    switchingMode.value = 'toKeyboard';

    // TikTok-модель: содержимое панели (EmojiPicker) убираем из DOM МГНОВЕННО (KeepAlive сохраняет экземпляр)
    // (на Android visualViewport шлёт resize только в конце анимации, поэтому
    // удерживаемый смонтированный пикер весь слайд висел бы поверх клавиатуры).
    // Сама панель при этом ОСТАЁТСЯ в потоке с высотой K - inset (is-open +
    // инлайн-высота через holdEmojiUntilKeyboard) — пустой прозрачный бокс,
    // который держит композер на месте, пока клавиатура не займёт его место.
    // Удержание снимается, когда inset достигнет K или клавиатура осядет
    // (см. updateKeyboardInset и holdFallbackTimerId ниже).
    holdEmojiUntilKeyboard.value = true;
    showEmojiPicker.value = false;

    // Fallback: если клавиатура не откроется (например, пользователь быстро сбросил
    // фокус), снимаем режим переключения через 300мс.
    startSwitchingModeTimeout();

    // Watchdog удержания: если ни одного resize-инсета так и не пришло
    // (focus не поднял клавиатуру), через 900мс снимаем hold — композер
    // вернётся к нижнему краю. Без этого спейсер зависал бы навсегда.
    if (holdFallbackTimerId) clearTimeout(holdFallbackTimerId);
    holdFallbackTimerId = setTimeout(() => {
      holdFallbackTimerId = null;
      if (holdEmojiUntilKeyboard.value && keyboardInsetPx.value === 0) {
        holdEmojiUntilKeyboard.value = false;
      }
    }, 900);

    nextTick(() => {
      textareaRef.value?.focus();
    });
  }

  // Страховка: часть браузеров не шлёт vv-события на открытие клавиатуры.
  // Перечитываем vv/ICB позже, когда клавиатура уже поднялась (или не поднялась) —
  // иначе панель-спейсер оставалась висеть между клавиатурой и композером.
  if (isMobile.value) {
    window.setTimeout(() => updateKeyboardInset(), 350);
  }
};

const handleTextareaBlur = () => {
  // Останавливаем typing когда пользователь покидает поле ввода
  if (props.currentContactId) {
    typing.stopTyping(props.currentContactId);
  }
  // Страховка: если vv-события о закрытии клавиатуры не пришли, перечитываем
  // позже — спейсер должен схлопнуться вместе с клавиатурой.
  if (isMobile.value) {
    window.setTimeout(() => updateKeyboardInset(), 350);
  }
};

// Fallback timeout to reset switchingMode if visualViewport doesn't report expected change.
let switchingModeTimeoutId: ReturnType<typeof setTimeout> | null = null;
// Watchdog для holdEmojiUntilKeyboard: снимает «спейсер», если клавиатура не открылась.
let holdFallbackTimerId: ReturnType<typeof setTimeout> | null = null;

const startSwitchingModeTimeout = () => {
  if (switchingModeTimeoutId) clearTimeout(switchingModeTimeoutId);
  switchingModeTimeoutId = setTimeout(() => {
    if (switchingMode.value !== 'none') {
      switchingMode.value = 'none';
      // frozenInsetPx не обнуляем: если клавиатура появится позже таймаута,
      // проверка inset >= frozen-32 в updateKeyboardInset всё ещё нужна,
      // чтобы корректно закрыть удерживаемый emoji-пикер.
    }
    switchingModeTimeoutId = null;
  }, 300);
};

const toggleEmojiPicker = () => {
  // Mobile: Viber стиль с полной заморозкой позиции
  if (isMobile.value) {
    // Если emoji picker открыт - переключаемся на клавиатуру: фокус textarea запускает handleTextareaFocus
    if (showEmojiPicker.value) {
      textareaRef.value?.focus();
      return;
    }

    // Если emoji picker закрыт - открываем его
    showEmojiPicker.value = true;

    // Клавиатура открыта (или ещё поднимается после недавнего emoji→keyboard):
    // убираем фокус с textarea, чтобы она закрылась — панель плавно вырастет
    // 0→K по мере убывания инсета (Viber переход).
    if (isKeyboardOpen.value || holdEmojiUntilKeyboard.value || document.activeElement === textareaRef.value) {
      holdEmojiUntilKeyboard.value = false;
      textareaRef.value?.blur();
    }

    // ВСЕГДА замораживаем позицию на высоту emoji panel
    frozenInsetPx.value = emojiPanelOpenHeightPx.value;
    switchingMode.value = 'toEmoji';

    // Fallback: если клавиатура была закрыта, visualViewport НЕ пришлёт событие
    // закрытия (inset и так 0) → `switchingMode='toEmoji'` застрянет навсегда.
    // Таймаут гарантирует возврат в состояние 'none' через 300мс в любом случае.
    startSwitchingModeTimeout();

    nextTick(() => updateMeasuredEmojiPanelHeight());
    return;
  }

  // Desktop/default: just toggle emoji panel.
  const next = !showEmojiPicker.value;
  showEmojiPicker.value = next;
  if (next) {
    nextTick(() => updateMeasuredEmojiPanelHeight());
  }
};

const closeEmojiPicker = () => {
  showEmojiPicker.value = false;
  // Сбрасываем заморозку при явном закрытии
  switchingMode.value = 'none';
  frozenInsetPx.value = 0;
  holdEmojiUntilKeyboard.value = false;
};

const addEmoji = (emoji: string) => {
  const textarea = textareaRef.value;
  
  // На мобильных устройствах вставляем emoji в позицию курсора
  if (isMobile.value && textarea) {
    // Сохраняем текущую позицию курсора
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const textBefore = message.value.substring(0, start);
    const textAfter = message.value.substring(end);
    
    // Вставляем emoji в позицию курсора
    message.value = textBefore + emoji + textAfter;
    
    nextTick(() => {
      // Восстанавливаем позицию курсора после вставки
      const newCursorPos = start + emoji.length;
      textarea.setSelectionRange(newCursorPos, newCursorPos);
      
      // На мобильных НЕ фокусируемся на textarea, чтобы не появлялась клавиатура
      // На десктопе фокусируемся для удобства
      if (!isMobile.value) {
        textarea.focus();
      }
      
      // Корректируем высоту textarea
      adjustTextareaHeight();
    });
  } else {
    // На десктопе также вставляем emoji в позицию курсора
    if (textarea) {
      // Сохраняем текущую позицию курсора
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const textBefore = message.value.substring(0, start);
      const textAfter = message.value.substring(end);
      
      // Вставляем emoji в позицию курсора
      message.value = textBefore + emoji + textAfter;
      
      nextTick(() => {
        // Восстанавливаем позицию курсора после вставки
        const newCursorPos = start + emoji.length;
        textarea.setSelectionRange(newCursorPos, newCursorPos);
        textarea.focus();
        
        // Корректируем высоту textarea
        adjustTextareaHeight();
      });
    } else {
      // Fallback: если textarea недоступен, добавляем в конец
      message.value += emoji;
      nextTick(() => {
        adjustTextareaHeight();
      });
    }
  }
  
  // На мобильных НЕ закрываем emoji picker при выборе emoji - пользователь может выбрать несколько emoji
  // На десктопе НЕ закрываем автоматически - только по кнопке или клику вне компонента
};

const triggerFileUpload = () => {
  fileInput.value?.click();
};

const handleFileUpload = (event: Event) => {
  const target = event.target as HTMLInputElement;
  const files = Array.from(target.files || []);
  
  if (files.length === 0) return;
  
  // Ограничиваем количество файлов до 5 как в AngularJS
  const filesToProcess = files.slice(0, 5);
  
  if (files.length > 5) {
    logger.warn('Maximum 5 files allowed. Only first 5 will be uploaded.');
  }
  
  uploading.value = true;
  let processedCount = 0;
  
  filesToProcess.forEach(file => {
    // Проверяем что это изображение
    if (!file.type.startsWith('image/')) {
      logger.warn('Only image files are allowed:', file.name);
      processedCount++;
      if (processedCount === filesToProcess.length) {
        uploading.value = false;
      }
      return;
    }
    
    // Проверяем размер файла (максимум 10MB)
    const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
    if (file.size > MAX_FILE_SIZE) {
      logger.warn('File too large (max 10MB):', file.name);
      processedCount++;
      if (processedCount === filesToProcess.length) {
        uploading.value = false;
      }
      return;
    }
    
    // Создаем превью
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const objectUrl = URL.createObjectURL(file);
        uploadedFiles.value.push({
          file,
          preview: e.target?.result as string,
          name: file.name,
          objectUrl
        });
        // v-else-if превью рендерится только когда emoji-picker закрыт —
        // иначе при прикреплении файла поверх открытой emoji-панели
        // медиа-блок не появится. Закрываем пикер.
        if (showEmojiPicker.value) closeEmojiPicker();
        logger.log('Created object URL for:', file.name);
      } catch (error) {
        console.error('Failed to create object URL for:', file.name, error);
      } finally {
        processedCount++;
        if (processedCount === filesToProcess.length) {
          uploading.value = false;
        }
      }
    };
    
    reader.onerror = () => {
      console.error('Failed to read file:', file.name);
      processedCount++;
      if (processedCount === filesToProcess.length) {
        uploading.value = false;
      }
    };
    
    reader.readAsDataURL(file);
  });
  
  // Сбрасываем input
  target.value = '';
};

const removeFile = (index: number) => {
  const item = uploadedFiles.value[index];
  
  // Revoke object URL before removing from array
  try {
    if (item?.objectUrl) {
      URL.revokeObjectURL(item.objectUrl);
      logger.log('Revoked object URL for removed file:', item.name);
    }
  } catch (error) {
    logger.warn('Failed to revoke object URL for removed file:', item.name, error);
  }
  
  // Remove from array
  uploadedFiles.value.splice(index, 1);
};

// Закрываем emoji picker при клике вне компонента
const handleGlobalClick = (e: Event) => {
  const target = e.target as Element;
  
  // Не закрывать при клике на кнопку emoji или внутри emoji picker
  if (target?.closest('.btn-emoji-picker') || target?.closest('.emoji-picker')) {
    return;
  }
  
  // На мобильных не закрывать при клике внутри conversation-input (для TikTok-like поведения)
  // На десктопе тоже не закрывать при клике внутри conversation-input
  if (target?.closest('.conversation-input')) {
    return;
  }
  
  // Закрывать только при клике вне conversation-input
  if (showEmojiPicker.value) {
    showEmojiPicker.value = false;
    // Сбрасываем заморозку при закрытии извне
    switchingMode.value = 'none';
    frozenInsetPx.value = 0;
    holdEmojiUntilKeyboard.value = false;
  }
};

onMounted(() => {
  document.addEventListener('click', handleGlobalClick);
  nextTick(() => adjustTextareaHeight());

  nextTick(() => updateMeasuredEmojiPanelHeight());

  let ro: ResizeObserver | null = null;
  if (typeof ResizeObserver !== 'undefined') {
    ro = new ResizeObserver(() => updateMeasuredEmojiPanelHeight());
    if (emojiPanelRef.value) {
      ro.observe(emojiPanelRef.value);
    }
  }

  if (window.visualViewport) {
    window.visualViewport.addEventListener('resize', updateKeyboardInset);
    window.visualViewport.addEventListener('scroll', updateKeyboardInset);
    updateKeyboardInset();
  }

  // Реактивность `isMobile` при повороте устройства / ресайзе окна.
  const handleWindowResize = () => {
    viewportWidthPx.value = window.innerWidth;
    // window resize = изменение layout viewport (resizes-content сжимает ICB
    // вместе с клавиатурой): перечитываем vv/ICB. На части устройств vv-событие
    // приходит раньше, чем фактически применяется усадка ICB, и без этого
    // перечита layoutExcludedPx оставался нулём — панель-спейсер зависала
    // между клавиатурой и композером (пустой зазор).
    updateKeyboardInset();
  };
  window.addEventListener('resize', handleWindowResize);
  window.addEventListener('orientationchange', handleWindowResize);
  (window as any).__messagesWindowResizeHandler = handleWindowResize;

  // keep reference for cleanup
  (window as any).__messagesEmojiPanelRO = ro;
  
  // Add beforeunload listener to cleanup object URLs
  const handleBeforeUnload = () => {
    revokeObjectUrls();
  };
  window.addEventListener('beforeunload', handleBeforeUnload);
  
  // Store reference for cleanup
  (window as any).__messagesBeforeUnloadHandler = handleBeforeUnload;
});

// Watch keyboard state changes to update emoji panel height
watch(
  [isKeyboardOpen, keyboardInsetPx, layoutExcludedPx, lastKeyboardInsetPx, showEmojiPicker, holdEmojiUntilKeyboard, () => uploadedFiles.value.length],
  () => {
    // Вызываем безусловно: функция сама решает ставить или ЧИСТИТЬ инлайн-
    // высоту панели. При закрытии без этого условия stale-height оставался
    // бы на элементе до следующего открытия.
    if (isMobile.value) {
      nextTick(() => updateMeasuredEmojiPanelHeight());
    }
  },
  { immediate: true }
);

onUnmounted(() => {
  // Persist current draft before unmount
  if (draftSaveTimeout) {
    clearTimeout(draftSaveTimeout);
    draftSaveTimeout = null;
  }
  if (props.currentContactId && !isEditing.value) {
    saveDraftNow(props.currentContactId, message.value);
  }

  document.removeEventListener('click', handleGlobalClick);
  revokeObjectUrls();
  
  // Clear the uploaded files array to remove references
  uploadedFiles.value = [];

  const ro = (window as any).__messagesEmojiPanelRO as ResizeObserver | null;
  if (ro) {
    ro.disconnect();
    (window as any).__messagesEmojiPanelRO = null;
  }

  if (window.visualViewport) {
    window.visualViewport.removeEventListener('resize', updateKeyboardInset);
    window.visualViewport.removeEventListener('scroll', updateKeyboardInset);
  }

  // Clean up window resize listeners (реактивность isMobile).
  const windowResizeHandler = (window as any).__messagesWindowResizeHandler;
  if (windowResizeHandler) {
    window.removeEventListener('resize', windowResizeHandler);
    window.removeEventListener('orientationchange', windowResizeHandler);
    (window as any).__messagesWindowResizeHandler = null;
  }

  // Очистить pending switching-mode таймаут, чтобы он не сработал после unmount.
  if (switchingModeTimeoutId) {
    clearTimeout(switchingModeTimeoutId);
    switchingModeTimeoutId = null;
  }

  if (holdFallbackTimerId) {
    clearTimeout(holdFallbackTimerId);
    holdFallbackTimerId = null;
  }

  if (keyboardSettleTimerId) {
    clearTimeout(keyboardSettleTimerId);
    keyboardSettleTimerId = null;
  }

  // Remove beforeunload listener
  const beforeUnloadHandler = (window as any).__messagesBeforeUnloadHandler;
  if (beforeUnloadHandler) {
    window.removeEventListener('beforeunload', beforeUnloadHandler);
    (window as any).__messagesBeforeUnloadHandler = null;
  }
});

watch(
  () => props.replyTo,
  (newReplyTo) => {
    if (newReplyTo) {
      // Фокус на textarea при активации reply mode
      nextTick(() => {
        textareaRef.value?.focus();
      });
    } else {
      // Убрать фокус и скрыть клавиатуру при отмене reply
      nextTick(() => {
        textareaRef.value?.blur();
      });
    }
  }
);

// Load draft when switching contacts
watch(
  () => props.currentContactId,
  (newId, oldId) => {
    if (oldId && !isEditing.value) {
      saveDraftNow(oldId, message.value);
    }
    if (newId && !isEditing.value) {
      message.value = loadDraft(newId);
      nextTick(() => {
        adjustTextareaHeight();
      });
    }
  },
  { immediate: true }
);

// Switch between edit text and draft text
watch(
  () => props.editText,
  (newText, oldText) => {
    if (newText === undefined || newText === null) return;
    if (newText === '') {
      // Editing cancelled (not initial mount): restore draft for current contact
      if (oldText !== undefined && oldText !== '' && props.currentContactId) {
        message.value = loadDraft(props.currentContactId);
        nextTick(() => {
          adjustTextareaHeight();
        });
      }
      return;
    }
    message.value = newText;
    nextTick(() => {
      adjustTextareaHeight();
      textareaRef.value?.focus();
    });
  },
  { immediate: true }
);

// Auto-save draft while typing
watch(
  message,
  (newText) => {
    if (!props.currentContactId) return;
    if (isEditing.value) return;
    debouncedSaveDraft(props.currentContactId, newText);
  }
);
</script>

<style scoped>

.conversation-input {
  --composer-inset: 0px;
  --keyboard-inset: 0px;
  --bottom-inset: 0px;
  --emoji-panel-max-height: 180px;
  --emoji-picker-height: 164px;
  /* Viber стиль: строка ввода двигается вверх/вниз */
  padding-bottom: env(safe-area-inset-bottom, 0px) !important;
  transition: height .2s ease;
  min-height: 64px;
  height: auto !important;
}

.composer-main {
  /* Viber стиль: строка ввода внизу, поднимается при открытии панелей */
  position: sticky;
  bottom: calc(var(--bottom-inset, 0px) + env(safe-area-inset-bottom, 0px));
  z-index: 2;
  transition: bottom 120ms ease;
}

@media (max-width: 767.98px) {
  /* Viber стиль: убираем transition на мобильных для статичности строки ввода */
  .composer-main {
    transition: none;
  }
  
  /* Дополнительная стабилизация позиции при переключениях */
  .conversation-input.keyboard-open .composer-main {
    transition: none;
  }
  
  .conversation-input.emoji-open .composer-main {
    transition: none;
  }
}

.conversation-input .message-input {
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
  resize: none;
  overflow-y: hidden;
  min-height: 38px;
}

.conversation-input .input-group-append .btn {
  height: 38px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding-top: 0;
  padding-bottom: 0;
}

.conversation-input.emoji-open {
  height: auto;
}

.uploaded-files-preview {
  overflow-x: auto;
  overflow-y: hidden;
  /* Убираем padding, border и background так как используем reply-quote-bar */
}

.file-preview-item {
  display: inline-block;
  position: relative;
}

/* Отступы ряда превью через gap (на мобиле плитки считаются от ширины ряда) */
.file-preview-row {
  gap: 8px;
}

.file-preview-thumb {
  width: 64px;
  height: 64px;
  object-fit: cover;
}

/* Кнопка удаления вложения — в стиле оверлеев медиа (тёмный полупрозрачный
   круг + белый fe-x, как индикатор загрузки в MessageList), вместо красного
   btn-danger, выбивающегося из палитры мессенджера */
.file-preview-remove {
  top: -6px;
  right: -6px;
  width: 20px;
  height: 20px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  line-height: 1;
  cursor: pointer;
}

/* Плитка «добавить ещё фото» — в стиле плитки загрузки на /settings/photos
   (.upload-kit-input: пунктирная рамка 2px, серый плюс, прозрачный фон) */
.file-preview-add {
  flex-shrink: 0;
  width: 64px;
  height: 64px;
  padding: 0;
  border: 2px dashed #adb5bd;
  border-radius: 7px;
  background: transparent;
  color: #adb5bd;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  line-height: 1;
  cursor: pointer;
  transition: color 0.15s ease, border-color 0.15s ease;
}

.file-preview-add:hover,
.file-preview-add:focus {
  color: #c2c8d2;
  border-color: #c2c8d2;
}

@media (max-width: 767.98px) {
  /* Мобила: 3 карточки на ширину ряда */
  .file-preview-item {
    flex: 0 0 calc((100% - 16px) / 3);
    aspect-ratio: 1;
    display: block;
  }

  .file-preview-thumb {
    width: 100%;
    height: 100%;
  }

  .file-preview-remove {
    top: -7px;
    right: -7px;
    width: 24px;
    height: 24px;
    font-size: 14px;
  }

  .file-preview-add {
    flex: 0 0 calc((100% - 16px) / 3);
    aspect-ratio: 1;
    width: auto;
    height: auto;
    font-size: 32px;
  }
}

.emoji-panel {
  max-height: 0;
  overflow: hidden;
  margin-top: 0;
  opacity: 0;
  will-change: max-height, opacity;
  transition:
    max-height 0.9s cubic-bezier(0.05, 0.65, 0.15, 1.0),
    opacity 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.emoji-panel.is-open {
  max-height: var(--emoji-panel-max-height, 180px);
  overflow: hidden;
  /* margin-top: 0 — панель должна занимать ровно высоту клавиатуры, чтобы
     композер стоял на той же Y-позиции, что и при открытой клавиатуре */
  margin-top: 0;
  opacity: 1;
  transition:
    max-height 0.9s cubic-bezier(0.05, 0.65, 0.15, 1.0),
    opacity 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

@media (max-width: 767.98px) {
  /* Use JS-calculated panel height for consistent sizing */
  .emoji-panel.is-open {
    max-height: var(--emoji-panel-max-height, 180px);
  }

  /* Предотвращение прокрутки при открытой emoji panel */
  .emoji-panel.is-open {
    position: relative;
    z-index: 1000;
  }
}

/* Во время переключения keyboard ↔ emoji высота панели обновляется
   на каждом кадре анимации клавиатуры (K - inset) — CSS-transition
   только отставал бы и давал лишние промежуточные высоты. */
.conversation-input .emoji-panel.switching {
  transition: none !important;
}

/* Пока клавиатура открыта, панель-спейсер тоже живёт на live-инсете —
   без анимации, чтобы точно следовать за клавиатурой. */
.conversation-input .emoji-panel.keyboard-open {
  transition: none !important;
}

/* Во время переключения keyboard↔emoji убираем анимацию bottom у композера:
   sticky-offset уже привязан к живому инсету, transition 120ms только
   добавлял бы видимый дрифт позиции. */
.conversation-input.switching .composer-main {
  transition: none !important;
}

.emoji-panel :deep(.emoji-picker) {
  position: static;
  bottom: auto;
  right: auto;
  left: auto;
  transform: none;
  max-width: none;
  width: 100%;
  max-height: none;
  overflow: hidden;
  margin: 0;
  /* background-color: var(--bs-body-bg, #fff); */
  /* border: 1px solid var(--bs-border-color, #dee2e6); */
  /* border-radius: var(--bs-border-radius, 0.375rem); */
  /* box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075); */
  padding: 0;
  scrollbar-width: auto;
  -ms-overflow-style: auto;
}

@media (min-width: 768px) {
  .emoji-panel :deep(.emoji-picker) {
    height: var(--emoji-picker-height, 164px);
  }
}

</style>
